import { useCallback, useEffect, useRef } from 'react';

import { getSiteComplianceJobPollingDelayMs } from '@/domains/siteCompliance';

interface PollingState {
  attempt: number;
  inFlight: boolean;
  timeoutId?: ReturnType<typeof setTimeout>;
}

interface UseJobPollingOptions<TJob, TResult> {
  getDelayMs?: (attempt: number) => number;
  getJobId: (job: TJob) => string;
  isActiveJob: (job: TJob) => boolean;
  isTerminalResult?: (result: TResult) => boolean;
  jobs: TJob[];
  onAfterPoll?: () => void;
  onJobError: (job: TJob, error: unknown) => void;
  onJobStatus: (job: TJob, result: TResult) => void;
  onPollDuration?: (
    job: TJob,
    durationMs: number,
    result: TResult | null,
    error: unknown,
  ) => void;
  pollJob: (job: TJob) => Promise<TResult>;
}

export const useJobPolling = <TJob, TResult>({
  getDelayMs = getSiteComplianceJobPollingDelayMs,
  getJobId,
  isActiveJob,
  isTerminalResult,
  jobs,
  onAfterPoll,
  onJobError,
  onJobStatus,
  onPollDuration,
  pollJob,
}: UseJobPollingOptions<TJob, TResult>) => {
  const pollingStateRef = useRef<Record<string, PollingState>>({});
  const optionsRef = useRef({
    getDelayMs,
    getJobId,
    isActiveJob,
    isTerminalResult,
    onAfterPoll,
    onJobError,
    onJobStatus,
    onPollDuration,
    pollJob,
  });
  const jobsRef = useRef(jobs);
  const pollJobStatusRef = useRef<(jobId: string) => void>(() => {});

  jobsRef.current = jobs;
  optionsRef.current = {
    getDelayMs,
    getJobId,
    isActiveJob,
    isTerminalResult,
    onAfterPoll,
    onJobError,
    onJobStatus,
    onPollDuration,
    pollJob,
  };

  const clearPolling = useCallback((jobId: string) => {
    const state = pollingStateRef.current[jobId];

    if (state?.timeoutId) {
      clearTimeout(state.timeoutId);
    }

    delete pollingStateRef.current[jobId];
  }, []);

  const getActiveJob = useCallback((jobId: string) => {
    const { getJobId: resolveJobId, isActiveJob: isActive } =
      optionsRef.current;

    return jobsRef.current.find(
      (job) => resolveJobId(job) === jobId && isActive(job),
    );
  }, []);

  const scheduleNextPoll = useCallback(
    (jobId: string, delayMs: number) => {
      const state =
        pollingStateRef.current[jobId] ??
        (pollingStateRef.current[jobId] = {
          attempt: 0,
          inFlight: false,
        });

      if (state.timeoutId) {
        clearTimeout(state.timeoutId);
      }

      state.timeoutId = setTimeout(() => {
        pollJobStatusRef.current(jobId);
      }, delayMs);
    },
    [],
  );

  const pollJobStatus = useCallback(async (jobId: string) => {
    const state = pollingStateRef.current[jobId];

    if (!state || state.inFlight) {
      return;
    }

    const job = getActiveJob(jobId);

    if (!job) {
      clearPolling(jobId);
      return;
    }

    state.inFlight = true;
    let shouldContinue = true;
    let result: TResult | null = null;
    let pollError: unknown = null;
    const startedAt = Date.now();

    try {
      result = await optionsRef.current.pollJob(job);
      optionsRef.current.onJobStatus(job, result);

      if (optionsRef.current.isTerminalResult?.(result)) {
        shouldContinue = false;
      }
    } catch (error) {
      pollError = error;
      optionsRef.current.onJobError(job, error);
      shouldContinue = false;
    } finally {
      optionsRef.current.onPollDuration?.(
        job,
        Math.round(Date.now() - startedAt),
        result,
        pollError,
      );
      state.inFlight = false;
      optionsRef.current.onAfterPoll?.();

      const shouldScheduleNextPoll = shouldContinue && Boolean(getActiveJob(jobId));

      if (!shouldScheduleNextPoll) {
        clearPolling(jobId);
      } else {
        const delayMs = optionsRef.current.getDelayMs(state.attempt);
        state.attempt += 1;
        scheduleNextPoll(jobId, delayMs);
      }
    }
  }, [clearPolling, getActiveJob, scheduleNextPoll]);

  pollJobStatusRef.current = (jobId: string) => {
    void pollJobStatus(jobId);
  };

  useEffect(() => {
    const activeJobs = jobs.filter(isActiveJob);
    const activeJobIds = new Set(activeJobs.map(getJobId));

    activeJobs.forEach((job) => {
      const jobId = getJobId(job);

      if (!pollingStateRef.current[jobId]) {
        pollingStateRef.current[jobId] = {
          attempt: 0,
          inFlight: false,
        };
        scheduleNextPoll(jobId, 0);
      }
    });

    Object.keys(pollingStateRef.current).forEach((jobId) => {
      if (!activeJobIds.has(jobId)) {
        clearPolling(jobId);
      }
    });
  }, [clearPolling, getJobId, isActiveJob, jobs, scheduleNextPoll]);

  useEffect(() => {
    return () => {
      Object.keys(pollingStateRef.current).forEach((jobId) => {
        clearPolling(jobId);
      });
    };
  }, [clearPolling]);

  return {
    clearPolling,
  };
};
